import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Badge
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Timeline as TimelineIcon,
  Domain as DomainIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Microsoft as MicrosoftIcon,
  Verified as VerifiedIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import api from '../api';
import logger from './../utils/logger.js';

const DNSChangeTracker = () => {
  // State for data
  const [changes, setChanges] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState(null);
  
  // State for scheduler status
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerLoading, setSchedulerLoading] = useState(false);
  
  // State for UI
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  
  // State for filters
  const [filterParams, setFilterParams] = useState({
    page: 1,
    per_page: 25,
    domain: '',
    change_type: '',
    change_category: '',
    significance: '',
    days_back: 30
  });
  
  // State for pagination
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  
  // State for domain history dialog
  const [domainHistoryOpen, setDomainHistoryOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [domainHistory, setDomainHistory] = useState(null);
  const [domainHistoryLoading, setDomainHistoryLoading] = useState(false);

  // Load data on component mount and when filters change
  useEffect(() => {
    loadData();
    loadStats();
    loadFilters();
    loadSchedulerStatus();
  }, []);

  useEffect(() => {
    if (tabValue === 0) {
      loadData();
    }
  }, [filterParams, tabValue]);

  // Auto-refresh scheduler status every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadSchedulerStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.getDnsChanges(filterParams);
      if (response.success) {
        setChanges(response.data.changes);
        setPagination(response.data.pagination);
      } else {
        setError(response.message || 'Failed to load DNS changes');
      }
    } catch (err) {
      logger.error('Error loading DNS changes:', err);
      setError('Failed to load DNS changes');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    setStatsLoading(true);
    
    try {
      const response = await api.getDnsChangeStats(filterParams.days_back);
      if (response.success) {
        setStats(response.data);
      } else {
        logger.error('Failed to load DNS change stats:', response.message);
      }
    } catch (err) {
      logger.error('Error loading DNS change stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadFilters = async () => {
    try {
      const response = await api.getDnsChangeFilters();
      if (response.success) {
        setFilters(response.data);
      }
    } catch (err) {
      logger.error('Error loading DNS change filters:', err);
    }
  };

  const loadSchedulerStatus = async () => {
    setSchedulerLoading(true);
    
    try {
      const response = await api.getDnsSchedulerStatus();
      if (response.success) {
        setSchedulerStatus(response.data);
      } else {
        logger.error('Failed to load scheduler status:', response.message);
        // Don't show scheduler status if user doesn't have access
        setSchedulerStatus(null);
      }
    } catch (err) {
      logger.error('Error loading scheduler status:', err);
      // Handle authentication/authorization errors gracefully
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        logger.log('User does not have admin access to view scheduler status');
        setSchedulerStatus(null);
      } else {
        logger.error('Unexpected error loading scheduler status:', err);
        setSchedulerStatus(null);
      }
    } finally {
      setSchedulerLoading(false);
    }
  };

  const loadDomainHistory = async (domain) => {
    setDomainHistoryLoading(true);
    
    try {
      const response = await api.getDomainChangeHistory(domain);
      if (response.success) {
        setDomainHistory(response.data);
      } else {
        logger.error('Failed to load domain history:', response.message);
      }
    } catch (err) {
      logger.error('Error loading domain history:', err);
    } finally {
      setDomainHistoryLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilterParams(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (event, newPage) => {
    setFilterParams(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleRefresh = () => {
    loadData();
    loadStats();
    loadSchedulerStatus();
  };

  const handleDomainClick = (domain) => {
    setSelectedDomain(domain);
    setDomainHistoryOpen(true);
    loadDomainHistory(domain);
  };

  const getSignificanceColor = (significance) => {
    switch (significance) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'email_provider': return <EmailIcon />;
      case 'security': return <SecurityIcon />;
      case 'microsoft': return <MicrosoftIcon />;
      case 'verification': return <VerifiedIcon />;
      default: return <DomainIcon />;
    }
  };

  const getSchedulerStatusColor = (status) => {
    switch (status) {
      case 'running': return 'success';
      case 'stopped': return 'default';
      case 'error': return 'error';
      case 'scheduled': return 'info';
      default: return 'default';
    }
  };

  const getSchedulerStatusIcon = (status) => {
    switch (status) {
      case 'running': return <PlayIcon />;
      case 'stopped': return <PauseIcon />;
      case 'error': return <ErrorIcon />;
      case 'scheduled': return <ScheduleIcon />;
      default: return <InfoIcon />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatChangeValue = (value) => {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value || 'N/A');
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon />
          DNS Change Tracking
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* DNS Scheduler Status Indicator */}
          {schedulerStatus && (
            <Card sx={{ minWidth: 300 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon fontSize="small" />
                    DNS Scheduler
                  </Typography>
                  {schedulerLoading && <CircularProgress size={16} />}
                </Box>
                
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Chip
                      icon={getSchedulerStatusIcon(schedulerStatus.status)}
                      label={schedulerStatus.status}
                      color={getSchedulerStatusColor(schedulerStatus.status)}
                      size="small"
                    />
                    {schedulerStatus.is_running && (
                      <Badge color="success" variant="dot">
                        <Typography variant="caption">Active</Typography>
                      </Badge>
                    )}
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {schedulerStatus.last_run_time ? (
                      `Last run: ${formatDate(schedulerStatus.last_run_time)}`
                    ) : (
                      'Never run'
                    )}
                  </Typography>
                  
                  {schedulerStatus.next_run_time && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Next run: {formatDate(schedulerStatus.next_run_time)}
                    </Typography>
                  )}
                  
                  {schedulerStatus.current_operation && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="primary">
                        Processing: {schedulerStatus.current_operation.domains_checked || 0}/
                        {schedulerStatus.current_operation.total_domains || 0} domains
                      </Typography>
                      {schedulerStatus.current_operation.percent_complete !== undefined && (
                        <LinearProgress 
                          variant="determinate" 
                          value={schedulerStatus.current_operation.percent_complete} 
                          sx={{ height: 4, mt: 0.5, borderRadius: 2 }}
                        />
                      )}
                    </Box>
                  )}
                  
                  {schedulerStatus.error && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      Error: {schedulerStatus.error}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
          
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={handleRefresh}
            disabled={loading || statsLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Change Log" />
        <Tab label="Statistics" />
        <Tab label="Analytics" />
      </Tabs>

      {/* Statistics Tab */}
      {tabValue === 1 && (
        <Box>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : stats ? (
            <Grid container spacing={3}>
              {/* Summary Cards */}
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      Total Changes
                    </Typography>
                    <Typography variant="h4">
                      {stats.summary.total_changes}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last {stats.summary.period_days} days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Changes by Category Chart */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Changes by Category
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={stats.changes_by_category}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ category, count }) => `${category}: ${count}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {stats.changes_by_category.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Changes by Significance */}
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      By Significance
                    </Typography>
                    {stats.changes_by_significance.map((item) => (
                      <Box key={item.significance} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">
                            {item.significance}
                          </Typography>
                          <Typography variant="body2">
                            {item.count}
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(item.count / stats.summary.total_changes) * 100} 
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>

              {/* Daily Changes Chart */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Changes Over Time
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={stats.daily_changes}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Most Active Domains */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Most Active Domains
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Domain</TableCell>
                            <TableCell align="right">Changes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stats.most_active_domains.map((domain) => (
                            <TableRow key={domain.domain}>
                              <TableCell>
                                <Button
                                  variant="text"
                                  onClick={() => handleDomainClick(domain.domain)}
                                  sx={{ textTransform: 'none' }}
                                >
                                  {domain.domain}
                                </Button>
                              </TableCell>
                              <TableCell align="right">{domain.change_count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Recent High-Significance Changes */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent High-Significance Changes
                    </Typography>
                    {stats.recent_high_significance.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No high-significance changes in the selected period
                      </Typography>
                    ) : (
                      stats.recent_high_significance.map((change) => (
                        <Box key={change.id} sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">
                              {change.domain}
                            </Typography>
                            <Chip 
                              label={change.significance_level} 
                              color={getSignificanceColor(change.significance_level)}
                              size="small"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {change.change_summary}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(change.detected_at)}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">No statistics available</Alert>
          )}
        </Box>
      )}

      {/* Change Log Tab */}
      {tabValue === 0 && (
        <Box>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon />
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Domain"
                  value={filterParams.domain}
                  onChange={(e) => handleFilterChange('domain', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Change Type</InputLabel>
                  <Select
                    value={filterParams.change_type}
                    onChange={(e) => handleFilterChange('change_type', e.target.value)}
                    label="Change Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    {filters?.change_types?.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filterParams.change_category}
                    onChange={(e) => handleFilterChange('change_category', e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="">All</MenuItem>
                    {filters?.change_categories?.map((category) => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Significance</InputLabel>
                  <Select
                    value={filterParams.significance}
                    onChange={(e) => handleFilterChange('significance', e.target.value)}
                    label="Significance"
                  >
                    <MenuItem value="">All</MenuItem>
                    {filters?.significance_levels?.map((level) => (
                      <MenuItem key={level} value={level}>{level}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Period</InputLabel>
                  <Select
                    value={filterParams.days_back}
                    onChange={(e) => handleFilterChange('days_back', e.target.value)}
                    label="Period"
                  >
                    {filters?.predefined_periods?.map((period) => (
                      <MenuItem key={period.days} value={period.days}>{period.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setFilterParams({
                    page: 1,
                    per_page: 25,
                    domain: '',
                    change_type: '',
                    change_category: '',
                    significance: '',
                    days_back: 30
                  })}
                  size="small"
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Changes Table */}
          <Paper>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Domain</TableCell>
                        <TableCell>Change</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Significance</TableCell>
                        <TableCell>Detected</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {changes.map((change) => (
                        <TableRow key={change.id}>
                          <TableCell>
                            <Button
                              variant="text"
                              onClick={() => handleDomainClick(change.domain)}
                              sx={{ textTransform: 'none' }}
                            >
                              {change.domain}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {change.change_summary}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getCategoryIcon(change.change_category)}
                              label={change.change_category}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={change.significance_level}
                              color={getSignificanceColor(change.significance_level)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(change.detected_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2">View Details</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Typography variant="subtitle2" gutterBottom>
                                  Previous Value:
                                </Typography>
                                <Typography variant="body2" component="pre" sx={{ mb: 2, fontSize: '0.8rem' }}>
                                  {formatChangeValue(change.previous_value)}
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom>
                                  New Value:
                                </Typography>
                                <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem' }}>
                                  {formatChangeValue(change.new_value)}
                                </Typography>
                                {change.change_description && (
                                  <>
                                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                      Description:
                                    </Typography>
                                    <Typography variant="body2">
                                      {change.change_description}
                                    </Typography>
                                  </>
                                )}
                              </AccordionDetails>
                            </Accordion>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <Pagination
                      count={pagination.total_pages}
                      page={pagination.page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>
      )}

      {/* Analytics Tab */}
      {tabValue === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Advanced analytics and trend analysis coming soon...
          </Alert>
          
          {stats && (
            <Grid container spacing={3}>
              {/* Change Types Bar Chart */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Change Types Distribution
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.changes_by_type}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <RechartsTooltip />
                        <Bar dataKey="count" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Domain History Dialog */}
      <Dialog
        open={domainHistoryOpen}
        onClose={() => setDomainHistoryOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Change History: {selectedDomain}
          </Typography>
          <IconButton onClick={() => setDomainHistoryOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {domainHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : domainHistory ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Total Changes: {domainHistory.total_changes}
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              {domainHistory.changes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No changes recorded for this domain
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Change</TableCell>
                        <TableCell>Significance</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {domainHistory.changes.map((change) => (
                        <TableRow key={change.id}>
                          <TableCell>
                            {formatDate(change.detected_at)}
                          </TableCell>
                          <TableCell>
                            {change.change_summary}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={change.significance_level}
                              color={getSignificanceColor(change.significance_level)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2">View</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem' }}>
                                  {change.change_description}
                                </Typography>
                              </AccordionDetails>
                            </Accordion>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : (
            <Alert severity="error">Failed to load domain history</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDomainHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DNSChangeTracker; 